import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "components/Graphs/Popover";
import { cx } from "components/Graphs/utils";
import * as Tooltip from "@radix-ui/react-tooltip";
import { Check, ChevronDown, Plus, Search, Settings } from "lucide-react";
import React from "react";

import OrganizationIcon from "../OrganizationIcon";
import type { ShellOrganization } from "../types";

export interface OrganizationContextSwitcherProps {
  organizations: readonly ShellOrganization[];
  activeOrganizationId: string;
  onOrganizationChange: (organization: ShellOrganization) => void;
  onCreateOrganization?: () => void;
  onManageOrganization?: () => void;
  defaultOpen?: boolean;
  className?: string;
}

const OrganizationMark: React.FC<{
  organization: ShellOrganization;
  className?: string;
}> = ({ organization, className }) => (
  <span
    className={cx(
      "relative flex size-5 shrink-0 items-center justify-center overflow-hidden rounded bg-highlight/[0.08] text-white",
      className,
    )}
    aria-hidden="true"
  >
    {organization.avatarUrl ? (
      <img
        src={organization.avatarUrl}
        alt=""
        className="size-full object-cover"
        draggable={false}
      />
    ) : (
      <OrganizationIcon className="size-5" />
    )}
  </span>
);

export const OrganizationContextSwitcher: React.FC<
  OrganizationContextSwitcherProps
> = ({
  organizations,
  activeOrganizationId,
  onOrganizationChange,
  onCreateOrganization,
  onManageOrganization,
  defaultOpen = false,
  className,
}) => {
  const [open, setOpen] = React.useState(defaultOpen);
  const [query, setQuery] = React.useState("");
  const headingId = React.useId();
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const activeOrganization =
    organizations.find(
      (organization) => organization.id === activeOrganizationId,
    ) ?? organizations[0];
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const matchingOrganizations = normalizedQuery
    ? organizations.filter(
        (organization) =>
          organization.id === query.trim() ||
          [organization.name, organization.slug, organization.plan]
            .filter(Boolean)
            .some((value) =>
              value?.toLocaleLowerCase().includes(normalizedQuery),
            ),
      )
    : organizations;
  const filteredOrganizations = [...matchingOrganizations].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
  );

  if (!activeOrganization) return null;

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) setQuery("");
  };

  const handleOrganizationChange = (organization: ShellOrganization) => {
    if (organization.disabled) return;
    onOrganizationChange(organization);
    handleOpenChange(false);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Switch organization, current organization ${activeOrganization.name}`}
          className={cx(
            "group flex h-9 min-w-0 max-w-[20rem] items-center gap-2 rounded border border-transparent px-1.5 text-left outline-none transition-colors",
            "hover:border-zinc-700 hover:bg-white/[0.035]",
            "focus-visible:border-highlight/50 focus-visible:ring-2 focus-visible:ring-highlight/35",
            "data-[state=open]:border-zinc-700 data-[state=open]:bg-white/[0.045]",
            className,
          )}
        >
          <OrganizationMark organization={activeOrganization} />
          <span className="hidden min-w-0 max-w-[10rem] truncate text-sm font-semibold text-zinc-100 sm:block md:max-w-[16rem]">
            {activeOrganization.name}
          </span>
          <ChevronDown
            className="hidden size-3 shrink-0 text-zinc-500 transition-colors group-hover:text-zinc-300 md:block"
            aria-hidden="true"
          />
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        sideOffset={8}
        collisionPadding={12}
        role="dialog"
        aria-labelledby={headingId}
        className="w-[min(24rem,calc(100vw-1.5rem))] border-zinc-700 bg-[#0b0e0c] p-0 shadow-[0_18px_60px_rgba(0,0,0,0.52)]"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          searchInputRef.current?.focus();
        }}
      >
        <div className="flex min-h-12 items-center justify-between gap-3 border-b border-zinc-800 px-3 py-2">
          <h2 id={headingId} className="text-sm font-semibold text-zinc-100">
            Organizations
          </h2>
          {onManageOrganization ? (
            <Tooltip.Provider delayDuration={100}>
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <button
                    type="button"
                    aria-label={`Organization settings for ${activeOrganization.name}`}
                    className="flex size-8 shrink-0 items-center justify-center rounded text-zinc-400 outline-none transition-colors hover:bg-white/[0.05] hover:text-zinc-100 focus-visible:ring-2 focus-visible:ring-highlight/35"
                    onClick={() => {
                      handleOpenChange(false);
                      onManageOrganization();
                    }}
                  >
                    <Settings
                      className="size-4"
                      strokeWidth={1.7}
                      aria-hidden="true"
                    />
                  </button>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Content
                    side="bottom"
                    sideOffset={7}
                    collisionPadding={12}
                    className="z-[100] rounded border border-zinc-700 bg-zinc-950 px-3 py-2 text-xs leading-4 text-zinc-100 shadow-2xl"
                  >
                    Organization settings
                    <Tooltip.Arrow className="fill-zinc-950" />
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>
            </Tooltip.Provider>
          ) : null}
        </div>

        <div className="p-2">
          <label className="relative block">
            <span className="sr-only">Search organizations</span>
            <Search
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-zinc-500"
              aria-hidden="true"
            />
            <input
              ref={searchInputRef}
              type="search"
              value={query}
              onChange={(event) => setQuery(event.currentTarget.value)}
              placeholder="Find an organization…"
              aria-label="Search organizations"
              className="h-9 w-full rounded border border-zinc-700 bg-black/35 pl-9 pr-3 text-sm text-zinc-100 outline-none placeholder:text-zinc-600 focus:border-highlight/60 focus:ring-2 focus:ring-highlight/20"
            />
          </label>

          <div
            className="mt-2 max-h-72 space-y-1 overflow-y-auto"
            aria-label="Organizations"
          >
            {filteredOrganizations.length ? (
              filteredOrganizations.map((organization) => {
                const isActive = organization.id === activeOrganization.id;
                const metadata = [
                  organization.plan ? `${organization.plan} plan` : undefined,
                  organization.role ? `${organization.role} role` : undefined,
                ].filter(Boolean);

                return (
                  <button
                    key={organization.id}
                    type="button"
                    disabled={organization.disabled}
                    aria-label={`Switch to ${organization.name}${
                      metadata.length ? `, ${metadata.join(", ")}` : ""
                    }`}
                    aria-current={isActive ? "true" : undefined}
                    title={
                      organization.disabled
                        ? organization.disabledReason
                        : undefined
                    }
                    onClick={() => handleOrganizationChange(organization)}
                    className={cx(
                      "group flex w-full items-center gap-3 rounded border px-2.5 py-2 text-left outline-none transition-colors",
                      isActive
                        ? "border-highlight/25 bg-highlight/[0.075]"
                        : "border-transparent hover:border-zinc-800 hover:bg-white/[0.035]",
                      "focus-visible:border-highlight/50 focus-visible:ring-2 focus-visible:ring-highlight/25",
                      "disabled:cursor-not-allowed disabled:opacity-40",
                    )}
                  >
                    <OrganizationMark organization={organization} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium leading-5 text-zinc-100">
                        {organization.name}
                      </span>
                    </span>
                    {isActive ? (
                      <Check
                        className="size-4 shrink-0 text-highlight"
                        aria-hidden="true"
                      />
                    ) : null}
                  </button>
                );
              })
            ) : (
              <div className="rounded border border-dashed border-zinc-800 px-4 py-7 text-center">
                <p className="text-sm text-zinc-300">No organizations found</p>
                <p className="mt-1 text-xs text-zinc-400">
                  Try a different name or slug.
                </p>
              </div>
            )}
          </div>
          {onCreateOrganization ? (
            <div className="mt-2 border-t border-zinc-800 pt-2">
              <button
                type="button"
                className="flex w-full items-center justify-start gap-2 rounded px-2.5 py-2 text-sm font-semibold text-highlight outline-none transition-colors hover:bg-highlight/[0.08] focus-visible:ring-2 focus-visible:ring-highlight/35"
                onClick={() => {
                  handleOpenChange(false);
                  onCreateOrganization();
                }}
              >
                <Plus className="size-4 shrink-0" aria-hidden="true" />
                New organization
              </button>
            </div>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
};

OrganizationContextSwitcher.displayName = "OrganizationContextSwitcher";

export default OrganizationContextSwitcher;
